import {announce} from '@github-ui/aria-live'
import type {DashboardIssueIdentifier} from '@github-ui/shared-dashboard-lists/issues/types/list-types'
import type {
  DashboardIssueDeleteOptions,
  DashboardIssueDeletionConnection,
} from '../hooks/use-dashboard-issue-optimistic-updates'
import {
  FocusTrapManagementContextProvider,
  useFocusTrapManagementContext,
} from '@github-ui/side-panel/FocusTrapManagementProvider'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link as RouterLink} from '@github-ui/react-router'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports -- Relay-free skeleton subpath; keeps the lazy viewer chunk's loading state consistent without importing Relay.
import {IssueViewerLoading} from '@github-ui/issue-viewer/IssueViewerLoading'
import {SidePanel} from '@github-ui/side-panel'
import {StaleCacheBanner} from '@github-ui/stale-cache-banner'
import {GlobalCommands} from '@github-ui/ui-commands'
import {AlertIcon} from '@primer/octicons-react'
import {Link as PrimerLink} from '@primer/react'
import {type RefObject, Suspense, lazy, useCallback, useEffect, useMemo, useRef} from 'react'

import {useSiteHeaderHeight} from '../hooks/use-site-header-height'

// The Relay environment + IssueViewer (and their large dependency graph) load only when
// an issue is opened, keeping them out of the dashboard's base bundle.
const DashboardIssueViewerWrapper = lazy(async () => {
  const mod = await import('./DashboardIssueViewerWrapper')
  return {default: mod.DashboardIssueViewerWrapper}
})

// Matches what `DashboardIssueViewerWrapper` passes, so the skeleton shown while the chunk
// downloads is identical to the one the viewer then shows while its query runs.
const CHUNK_LOADING_OPTION_CONFIG = {useViewportQueries: false}

/**
 * Renders a dashboard issue in a side panel. Matching the issues dashboard, the `SidePanel`
 * is only mounted while an issue is open — mounting it lazily is what lets the body-scroll lock
 * and focus trap release (and focus return) on close, and keeps browser-only work (the SidePanel's
 * `window.matchMedia`, the `useSiteHeaderHeight` DOM measurement) from running during server
 * rendering. The heavy Relay/IssueViewer body is additionally lazy-loaded inside it.
 */
export function DashboardIssueSidePanel({issue, ...props}: DashboardIssueSidePanelProps) {
  if (!issue) return null
  return (
    <FocusTrapManagementContextProvider>
      <DashboardIssueSidePanelContent issue={issue} {...props} />
    </FocusTrapManagementContextProvider>
  )
}

export function handleDashboardIssueDelete({
  issueId,
  options,
  onIssueDelete,
  onIssueChange,
  onClose,
}: {
  issueId: string
  options?: DashboardIssueDeleteOptions
  onIssueDelete?: (issueId: string, options?: DashboardIssueDeleteOptions) => void | Promise<void>
  onIssueChange?: () => void
  onClose: () => void
}): void | Promise<void> {
  if (onIssueDelete) return onIssueDelete(issueId, options)
  onIssueChange?.()
  onClose()
}

interface DashboardIssueSidePanelProps {
  /** The issue to show; `null` closes (and unmounts) the panel. */
  issue: DashboardIssueIdentifier | null
  /** Requests the panel to close (e.g. clears the owner's open issue). */
  onClose: () => void
  /** Clears the panel state before its issue is opened in the full-page route. */
  onExpand?: () => void
  /** Called after the issue is edited, so the dashboard list can refresh. */
  onIssueChange?: () => void
  /** Called after the issue is deleted, so the owner can settle caches, retarget focus, and close. */
  onIssueDelete?: (issueId: string, options?: DashboardIssueDeleteOptions) => void | Promise<void>
  issueDeletionConnection?: DashboardIssueDeletionConnection
  /** Opens the next issue in the list (j). PRs are skipped by the owner. */
  onNavigateToNextItem?: () => void
  /** Opens the previous issue in the list (k). PRs are skipped by the owner. */
  onNavigateToPreviousItem?: () => void
  /** Opens a clicked sub-issue in this same panel. */
  onSubIssueSelect?: (identifier: DashboardIssueIdentifier) => void
  /** Stable focus-return target; the owner may redirect focus to the original row. */
  returnFocusRef: RefObject<HTMLElement | null>
}

function DashboardIssueSidePanelContent({
  issue,
  onClose,
  onExpand,
  onIssueChange,
  onIssueDelete,
  issueDeletionConnection,
  onNavigateToNextItem,
  onNavigateToPreviousItem,
  onSubIssueSelect,
  returnFocusRef,
}: Omit<DashboardIssueSidePanelProps, 'issue'> & {issue: DashboardIssueIdentifier}) {
  const initialFocusRef = useRef<HTMLSpanElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const issueKey = `${issue.owner}/${issue.repo}/${issue.number}`
  const boundIssueDeletionConnection = useMemo<DashboardIssueDeletionConnection | undefined>(() => {
    if (!issueDeletionConnection) return undefined
    return {
      ...issueDeletionConnection,
      createRemoveOperation: nodeId =>
        issueDeletionConnection.createRemoveOperation?.(nodeId, {
          owner: issue.owner,
          repository: issue.repo,
          number: issue.number,
        }) ?? {
          type: 'removeFromConnection',
          connectionPath: issueDeletionConnection.connectionPath,
          nodeId,
        },
    }
  }, [issue.number, issue.owner, issue.repo, issueDeletionConnection])

  // The viewer body loads asynchronously (chunk, then query), so replace the removed spinner's
  // `srText` with one announcement per opened issue. Announced into the panel because a live
  // region outside the active dialog isn't reliably read.
  useEffect(() => {
    if (contentRef.current) announce('Loading issue', {element: contentRef.current})
  }, [issueKey])

  // Offset the panel below the global site header so it doesn't overlap it, matching the
  // issues dashboard side panel. (DOM-measuring hook; only runs once the panel is open.)
  const siteHeaderHeight = useSiteHeaderHeight()

  // Nested pickers/dialogs opened inside the IssueViewer (e.g. the label/assignee/milestone
  // pickers in `@github-ui/issue-actions`) flip this so the panel's own focus trap steps aside
  // while the picker is open, matching the issues dashboard side panel. The provider is mounted
  // by `DashboardIssueSidePanel`, so this reads the same context instance those pickers write.
  const {disableFocusTrap} = useFocusTrapManagementContext()

  // Reuse the issues dashboard's j/k keybindings (defined globally as `issues-react:*`) so
  // navigation between issues works identically here. The owner computes next/previous.
  const navigationCommands = useMemo(
    () => ({
      'issues-react:focus-next-issue': () => onNavigateToNextItem?.(),
      'issues-react:focus-previous-issue': () => onNavigateToPreviousItem?.(),
    }),
    [onNavigateToNextItem, onNavigateToPreviousItem],
  )

  // Deleting the issue is terminal: defer to the owner, or refresh and close.
  const handleIssueDelete = useCallback(
    (issueId: string, options?: DashboardIssueDeleteOptions) =>
      handleDashboardIssueDelete({issueId, options, onIssueDelete, onIssueChange, onClose}),
    [onClose, onIssueChange, onIssueDelete],
  )

  return (
    <SidePanel
      open
      onClose={onClose}
      returnFocusRef={returnFocusRef}
      initialFocusRef={initialFocusRef}
      defaultCloseElement="overlay"
      aria-label={`Issue #${issue.number}`}
      width="min(90%, 1280px)"
      topOffset={siteHeaderHeight}
      externalFocusTrapDisabled={disableFocusTrap}
    >
      <GlobalCommands commands={navigationCommands} />
      <div ref={contentRef} id="issue-viewer-side-panel" className="height-full overflow-y-auto">
        <span tabIndex={-1} ref={initialFocusRef} data-prevent-scroll-on-focus />
        <StaleCacheBanner />
        {/* Keyed by issue so j/k navigation to another issue mounts a fresh boundary: without it,
            one failed load would keep showing the fallback for every issue opened afterwards. */}
        <ErrorBoundary
          key={issueKey}
          boundaryName="DashboardIssueSidePanel"
          fallback={<IssueLoadError issue={issue} />}
        >
          <Suspense fallback={<IssueViewerLoading optionConfig={CHUNK_LOADING_OPTION_CONFIG} />}>
            <DashboardIssueViewerWrapper
              issue={issue}
              onClose={onClose}
              onExpand={onExpand}
              onIssueChange={onIssueChange}
              onIssueDelete={handleIssueDelete}
              issueDeletionConnection={boundIssueDeletionConnection}
              onSubIssueSelect={onSubIssueSelect}
            />
          </Suspense>
        </ErrorBoundary>
      </div>
    </SidePanel>
  )
}

// The lazy import (or the query) failed. Navigation was already prevented, so offer a
// plain link to open the issue the normal way.
function IssueLoadError({issue}: {issue: DashboardIssueIdentifier}) {
  const href = `/${issue.owner}/${issue.repo}/issues/${issue.number}`
  return (
    <div className="d-flex flex-column flex-items-start gap-2 p-4 f5 color-fg-muted">
      <span className="d-inline-flex flex-items-center gap-2">
        <AlertIcon className="color-fg-attention" />
        This issue couldn&apos;t be loaded here.
      </span>
      <PrimerLink as={RouterLink} to={href}>
        Open issue #{issue.number}
      </PrimerLink>
    </div>
  )
}
